import React, { useCallback, useEffect, useRef } from 'react';
import { rem } from 'design-system';
import Modal from 'utils/packages/Modal';
import Squircle from 'design-system/Squircle';
import IconInfoTriangle from 'design-system/icons/IconInfoTriangle';
import useColors from 'hooks/useColors';
import Amplitude from 'utils/amplitude';
import usePlaidLink from 'hooks/usePlaidLink';
import useLinkConnection from 'hooks/useLinkConnection';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { connectingSecondLogin } from 'login/actions/signinNavigation';
import { useAppDispatch } from 'store/hooks';
import useConnectionsLimitModal from './useConnectionsLimitModal';
import BankIssueModalContent from '../components/BankIssueModalContent';
const strings = {
    downModal: {
        title: 'Temporary bank Issue',
        description: (bankName) => `We may not be able to link accounts from ${bankName} due to degraded performance on their end.`,
        tryLater: 'Try later',
        continue: 'Continue',
    },
};
export const useOpenBankLoginForSecondConnection = (routeKey, connectAllBanks) => {
    const navigation = useMainStackNavigation();
    const colors = useColors();
    const dispatch = useAppDispatch();
    const connectingBankRef = useRef(undefined);
    const connectionsLimitModal = useConnectionsLimitModal();
    const disableCTA = useRef(false);
    const openPlaid = usePlaidLink();
    const timeoutRef = useRef(undefined);
    const onOpenPlaid = useCallback(() => {
        if (disableCTA.current) {
            return;
        }
        disableCTA.current = true;
        openPlaid();
        timeoutRef.current = setTimeout(() => {
            disableCTA.current = false;
        }, 1000);
    }, [openPlaid]);
    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);
    const { linkConnection } = useLinkConnection();
    const openBankLogin = useCallback((data) => {
        if (!connectAllBanks) {
            dispatch(connectingSecondLogin());
        }
        const { type } = data;
        connectingBankRef.current = data;
        const open = () => {
            if (data && data.is_plaid) {
                onOpenPlaid();
                return;
            }
            Amplitude.logEvent('OnBoarding.openBankLogin', {
                bankName: data,
                isLogged: false,
            });
            const navigateToConsent = (keyBack) => {
                const navigationParams = {
                    data,
                    back: true,
                    keyBack,
                    isConnectingSecondLogin: true,
                    connectAllBanks,
                };
                navigation.push('Consent', navigationParams);
            };
            switch (type) {
                case 'CRYPTO_WALLET':
                case 'CRYPTO_EXCHANGE':
                    if (data.loginType === 'OAUTH2') {
                        linkConnection(data, routeKey);
                    }
                    else {
                        navigateToConsent(routeKey);
                    }
                    break;
                case 'INVESTMENT':
                case 'PENSION':
                    linkConnection(data, routeKey);
                    break;
                default:
                    if (data.status?.consentStatus === 'DOWN') {
                        Modal.show(<BankIssueModalContent icon={<Squircle width={rem(64)} height={rem(64)} color={colors.status.informationalLight}>
                      <IconInfoTriangle width={rem(32)} height={rem(32)} color={colors.status.informational}/>
                    </Squircle>} strings={{
                                title: strings.downModal.title,
                                description: strings.downModal.description(data.name),
                                tryLater: strings.downModal.tryLater,
                                continue: strings.downModal.continue,
                            }} onPressContinue={() => {
                                Modal.hide(navigateToConsent);
                            }}/>);
                        return;
                    }
                    navigateToConsent();
                    break;
            }
        };
        connectionsLimitModal(open);
    }, [
        connectAllBanks,
        connectionsLimitModal,
        onOpenPlaid,
        linkConnection,
        routeKey,
        colors.status.informationalLight,
        colors.status.informational,
    ]);
    return openBankLogin;
};
